import { useEffect } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ONBOARDING_CONFIG } from '../../features/onboarding/constants';
import { MenuOnboarding } from '../../features/onboarding/MenuOnboarding';
import { useOnboarding } from '../../features/onboarding/useOnboarding';
import { useSideMenu } from '../../hooks/useSideMenu';
import { analytics } from '../../services/analytics';
import { LOG_OUT_LISTENERS, runLogOutSideEffects } from '../../services/shared';
import { useStores } from '../../stores/useStores';
import { BingoNotificationDot } from '../Notifications/BingoNotificationDot';
import { NotificationDotController } from '../Notifications/NotificationDotController';

import { Categories } from './Categories';
import { CMSLinks } from './CMSLinks';
import { Controls } from './Controls';

export const SideMenuContent = observer(() => {
  const { uiStore, onboardingStore, bingoStore } = useStores();
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { handleClose } = useSideMenu();

  useEffect(() => {
    LOG_OUT_LISTENERS.add(handleClose);

    return () => {
      LOG_OUT_LISTENERS.delete(handleClose);
    };
  }, [handleClose]);

  const tooltipPosition = onboardingStore.step === ONBOARDING_CONFIG.REDEEM ? 'bottom-start' : 'top-start';
  const offset: [number, number] = onboardingStore.step === ONBOARDING_CONFIG.REDEEM ? [0, 22] : [0, 15];

  const { showOnboarding, tooltipStyles, setReferenceElement, setPopperElement } = useOnboarding(
    ONBOARDING_CONFIG.MENU_ACTIONS,
    tooltipPosition,
    offset,
  );

  const openAccount = () => {
    void analytics.sendEvent('account_menu', {
      event_action: 'click',
      button_text: 'my_account',
      button_location: 'menu',
      click_id: CLICK_IDS.ACCOUNT.MENU_OPEN_ACCOUNT,
    });
    uiStore.setOpenFullPageDialog('account');
    handleClose();
  };

  const openPass = () => {
    void analytics.sendEvent('bingo_button_clicked', {
      triggered_by: 'menu_nav',
      event_action: 'click',
      event_category: 'metagame_bingo',
    });

    bingoStore.setBingoFtueEntryPoint('bingo');
    navigate('/bingo');
    handleClose();
  };

  const openRewards = () => {
    uiStore.setOpenFullPageDialog('rewards');
    handleClose();
    void analytics.sendEvent('rewards_menu', {
      event_action: 'click',
      button_location: 'menu',
    });
  };

  const isFtueRewardsStep = onboardingStore.step === ONBOARDING_CONFIG.REWARDS;

  return (
    <div className="side-menu__content">
      <Controls
        closeMenu={handleClose}
        setReferenceElement={onboardingStore.step === ONBOARDING_CONFIG.REDEEM ? setReferenceElement : null}
      />
      <Categories isFtueRewardsStep={isFtueRewardsStep} />
      <ul className="side-menu__links">
        <li>
          <button className="side-menu__action" onClick={openAccount}>
            {t('menu.myAccount')}
          </button>
        </li>
        {bingoStore.bingoEnabled && (
          <li>
            <button className="side-menu__action" onClick={openPass}>
              {t('menu.bingo')}
              <div className="dot_wrapper">
                <BingoNotificationDot type="small" />
              </div>
            </button>
          </li>
        )}
        <li>
          <button
            ref={isFtueRewardsStep ? setReferenceElement : null}
            className={classNames('side-menu__action', {
              'side-menu__action_onboarding': isFtueRewardsStep,
              'onboarding-arrow': isFtueRewardsStep,
            })}
            onClick={openRewards}
          >
            {t('rewards.title')}
            <div className="dot_wrapper">
              <NotificationDotController type="small" />
            </div>
          </button>
        </li>
        <CMSLinks />
        <li>
          <button className="side-menu__action" onClick={() => runLogOutSideEffects(true)}>
            {t('profile.logOut')}
          </button>
        </li>
      </ul>
      {showOnboarding && (
        <MenuOnboarding
          overlayClassName="side-menu__onboarding-overlay"
          setPopperElement={setPopperElement}
          tooltipStyles={tooltipStyles}
        />
      )}
    </div>
  );
});
